/**
 * Voice pipeline hook — multi-provider STT.
 *
 * Manages VAD, STT, and TTS in a unified voice pipeline.
 * Supports two STT providers:
 *
 * 1. **OpenAI Whisper** (primary) — when API key is in localStorage
 *    VAD speech end → encode WAV → Whisper API → transcript → chat.send
 * 2. **Web Speech API** (fallback) — no API key needed
 *    Runs alongside VAD, transcribes in real-time → finalize on speech end
 *
 * If Whisper fails (bad key, network), automatically falls back to
 * Web Speech API for the remainder of the session.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { VADManager } from '../voice/vad';
import {
  encodeAudioBlob,
  transcribeWithWhisper,
  getOpenAIKey,
  detectSttProvider,
  isWebSpeechAvailable,
  SpeechTranscriber,
} from '../voice/stt';
import type { SttProvider } from '../voice/stt';
import {
  TtsManager,
  fetchTtsConfig,
  stripMarkdownForTTS,
  splitIntoSentences,
  speakWithWebSpeech,
} from '../voice/tts';
import { getFSMActor } from './useFSM';
import { remoteLog } from '../utils/remoteLog';
import { AudioQueue } from '../voice/audio';
import type { WSClient } from '../ws/client';
import { DEFAULT_SESSION_KEY } from '../utils/constants';

export interface VoiceSegment {
  text: string;
  gateRejected?: boolean;
}

export type VoiceSegmentInput = string | VoiceSegment;

export interface UseVoiceOptions {
  /** WebSocket client for gateway communication */
  wsClient: WSClient | null;
  /** Session key for chat.send (default: from URL ?agent= param or "agent:main:main") */
  sessionKey?: string;
  /**
   * Called when a voice utterance has been transcribed and is ready to send.
   * The parent component should create a chat message and send via WS.
   *
   * @deprecated In Phase 1 turn-arbiter mode, use onSegment instead.
   *   onTranscript is still called if onSegment is NOT provided (backward compat).
   */
  onTranscript?: (text: string) => void;
  /**
   * Called when a single speech segment has been transcribed (turn arbiter mode).
   * The consumer should feed this into the TurnArbiter. Does NOT trigger chat.send.
   * Gate-rejected transcripts may carry metadata for narrow post-gate handling.
   */
  onSegment?: (segment: VoiceSegmentInput) => void;
  /**
   * Called with raw speech audio (Float32Array, 16kHz mono) when VAD
   * detects speech end. Used by Voice Lock for speaker verification.
   * Does NOT block the STT flow — runs asynchronously.
   */
  onSpeechAudio?: (audio: Float32Array) => void;
  /**
   * Optional async gate — called with raw speech audio before STT.
   * Return true to proceed with transcription, false to silently drop.
   * Used by Voice Lock to block unrecognized speakers.
   */
  shouldProcessAudio?: (audio: Float32Array) => Promise<boolean>;
  /** Language for Web Speech API (default: "en-US") */
  lang?: string;
  /** Called when TTS fails in a way the user must act on, e.g. expired backend auth. */
  onTtsError?: (message: string) => void;
}

export type SpeakResult = 'completed' | 'cancelled';

export interface SpeakTrackedOptions {
  onChunkComplete?: (chunkIndex: number) => void;
}

type VisibleTtsError = {
  userMessage: string;
  code?: string;
  provider?: string;
  status?: number;
};

function getVisibleTtsError(error: unknown): VisibleTtsError | null {
  if (typeof error !== 'object' || error === null) return null;
  const candidate = error as Record<string, unknown>;
  if (typeof candidate.userMessage !== 'string' || !candidate.userMessage.trim()) {
    return null;
  }
  return {
    userMessage: candidate.userMessage,
    code: typeof candidate.code === 'string' ? candidate.code : undefined,
    provider: typeof candidate.provider === 'string' ? candidate.provider : undefined,
    status: typeof candidate.status === 'number' ? candidate.status : undefined,
  };
}

function isSpeakerMutedInFSM(): boolean {
  const snapshot = getFSMActor().getSnapshot();
  const value = snapshot.value as Record<string, string>;
  return value.speakerMode === 'muted';
}

export interface UseVoiceReturn {
  /** Whether VAD is currently active (listening for speech) */
  isVADActive: boolean;
  /** Whether the user is currently speaking (speech detected) */
  isSpeaking: boolean;
  /** Whether TTS audio is currently playing */
  isTTSPlaying: boolean;
  /** Whether TTS playback is paused */
  isTTSPaused: boolean;
  /** Which STT provider is active */
  sttProvider: SttProvider;
  /** Whether any STT is available */
  sttAvailable: boolean;
  /** Interim transcript (from Web Speech API — Whisper doesn't have interim) */
  interimTranscript: string;
  /** Current playback speed */
  playbackSpeed: number;
  /** Current VAD sensitivity threshold */
  sensitivity: number;
  /** Whether adaptive VAD mode is enabled */
  isAdaptive: boolean;
  /** Start the voice pipeline (activate VAD + STT) */
  startListening: () => Promise<void>;
  /** Stop the voice pipeline (deactivate VAD + STT) */
  stopListening: () => void;
  /** Speak text through TTS pipeline (serialized — safe to call fire-and-forget) */
  speak: (text: string) => void;
  /** Speak text (or tracked chunks) and resolve when playback completes or is cancelled */
  speakTracked: (text: string | readonly string[], options?: SpeakTrackedOptions) => Promise<SpeakResult>;
  /** Stop TTS playback and clear queue */
  stopSpeaking: () => void;
  /** Pause TTS playback */
  pauseSpeaking: () => void;
  /** Resume TTS playback */
  resumeSpeaking: () => void;
  /** Skip current TTS segment */
  skipSegment: () => void;
  /** Set playback speed (1×–2×) */
  setPlaybackSpeed: (speed: number) => void;
  /** Cycle through speed presets */
  cycleSpeed: () => void;
  /** Set VAD sensitivity */
  setSensitivity: (threshold: number) => void;
  /** Toggle adaptive VAD mode */
  setAdaptive: (enabled: boolean) => void;
  /** Initialize TTS config from gateway */
  initTtsConfig: () => Promise<void>;
  /** Re-detect STT provider (call after API key changes) */
  refreshProvider: () => void;
  /** Reset TTS voice lock for a new response (call on stream start) */
  resetVoiceLock: (runId?: string) => void;
}

/**
 * Hook for managing the full voice pipeline:
 * VAD (voice detection) → STT (Whisper or Web Speech) → TTS (speech output)
 */
export function useVoice(options: UseVoiceOptions): UseVoiceReturn {
  const {
    wsClient,
    sessionKey = DEFAULT_SESSION_KEY,
    onTranscript,
    onSegment,
    onSpeechAudio,
    shouldProcessAudio,
    lang = 'en-US',
    onTtsError,
  } = options;

  // ── State ──
  const [isVADActive, setIsVADActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [isTTSPaused, setIsTTSPaused] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [playbackSpeed, setPlaybackSpeedState] = useState(1);
  const [sensitivity, setSensitivityState] = useState(0.8);
  const [isAdaptive, setIsAdaptiveState] = useState(true);
  const [sttProvider, setSttProvider] = useState<SttProvider>(() => detectSttProvider());

  // ── Refs ──
  const vadRef = useRef<VADManager | null>(null);
  const transcriberRef = useRef<SpeechTranscriber | null>(null);
  const ttsRef = useRef<TtsManager | null>(null);
  const audioQueueRef = useRef<AudioQueue | null>(null);
  const webSpeechRef = useRef<{ cancel: () => void } | null>(null);

  // Ref to the speak callback — allows flush code defined before speak()
  // to call it without triggering TS2448 (block-scoped variable used before declaration).
  const speakRef = useRef<((text: string) => void) | null>(null);

  // ── Speak serialization chain ──
  // Multiple speak() calls are chained so they execute sequentially,
  // guaranteeing playback order even when called fire-and-forget style.
  const speakChainRef = useRef<Promise<void>>(Promise.resolve());
  const speakCancellationVersionRef = useRef(0);
  const mutedCancellationVersionRef = useRef<number | null>(null);
  const speakTagCounterRef = useRef(0);
  const playbackWaitersRef = useRef<Map<string, () => void>>(new Map());
  const speakerMutedRef = useRef(isSpeakerMutedInFSM());

  // Keep callback/value refs current
  const wsClientRef = useRef(wsClient);
  wsClientRef.current = wsClient;
  const sessionKeyRef = useRef(sessionKey);
  sessionKeyRef.current = sessionKey;
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onSegmentRef = useRef(onSegment);
  onSegmentRef.current = onSegment;
  const onSpeechAudioRef = useRef(onSpeechAudio);
  onSpeechAudioRef.current = onSpeechAudio;
  const shouldProcessAudioRef = useRef(shouldProcessAudio);
  shouldProcessAudioRef.current = shouldProcessAudio;
  const sttProviderRef = useRef(sttProvider);
  sttProviderRef.current = sttProvider;

  const sttAvailable = sttProvider !== 'none';

  const syncSpeakerMutedRef = useCallback((): boolean => {
    const muted = isSpeakerMutedInFSM();
    speakerMutedRef.current = muted;
    return muted;
  }, []);

  const stopSpeakingInternal = useCallback((reason: 'explicit_stop' | 'speaker_muted') => {
    remoteLog('tts', 'stop_speaking', { reason });
    speakCancellationVersionRef.current += 1;
    mutedCancellationVersionRef.current = reason === 'speaker_muted'
      ? speakCancellationVersionRef.current
      : null;
    // Detach future speak() work from any stale in-flight serialization chain.
    // Old tasks still resolve in the background, but new responses can start
    // immediately on a fresh chain and stale tasks will self-cancel by version.
    speakChainRef.current = Promise.resolve();
    audioQueueRef.current?.stop();
    webSpeechRef.current?.cancel();
    webSpeechRef.current = null;
    for (const resolve of playbackWaitersRef.current.values()) {
      resolve();
    }
    playbackWaitersRef.current.clear();
    getFSMActor().send({ type: 'TTS_DEFERRED_FLUSH' });
    setIsTTSPlaying(false);
    setIsTTSPaused(false);
  }, []);

  // Initialize TtsManager and AudioQueue on mount
  useEffect(() => {
    if (!ttsRef.current) {
      ttsRef.current = new TtsManager();
    }

    if (!audioQueueRef.current) {
      audioQueueRef.current = new AudioQueue({
        onPlaybackStart: () => setIsTTSPlaying(true),
        onPlaybackEnd: () => {
          setIsTTSPlaying(false);
          setIsTTSPaused(false);
        },
        onItemEnd: (tag) => {
          if (!tag) return;
          const resolve = playbackWaitersRef.current.get(tag);
          if (!resolve) return;
          playbackWaitersRef.current.delete(tag);
          resolve();
        },
        onLog: (evt, data) => remoteLog('audioQ', evt, data),
      });
      setPlaybackSpeedState(audioQueueRef.current.speed);
    }

    return () => {
      vadRef.current?.destroy().catch(() => {});
      transcriberRef.current?.destroy();
      audioQueueRef.current?.stop();
      webSpeechRef.current?.cancel();
      for (const resolve of playbackWaitersRef.current.values()) {
        resolve();
      }
      playbackWaitersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const actor = getFSMActor();
    speakerMutedRef.current = isSpeakerMutedInFSM();

    const sub = actor.subscribe((snapshot) => {
      const value = snapshot.value as Record<string, string>;
      const nextMuted = value.speakerMode === 'muted';
      const wasMuted = speakerMutedRef.current;
      speakerMutedRef.current = nextMuted;

      if (nextMuted && !wasMuted) {
        stopSpeakingInternal('speaker_muted');
      }
    });

    return () => sub.unsubscribe();
  }, [stopSpeakingInternal]);

  // ── Fallback: Whisper → Web Speech for this session ──
  const fallbackToWebSpeech = useCallback(() => {
    if (isWebSpeechAvailable()) {
      console.warn('[useVoice] Falling back to Web Speech API for this session');
      setSttProvider('web-speech');
      sttProviderRef.current = 'web-speech';

      // If VAD is running, start the Web Speech transcriber
      if (vadRef.current?.isActive && !transcriberRef.current) {
        const transcriber = new SpeechTranscriber({
          lang,
          onInterim: (text) => setInterimTranscript(text),
        });
        if (transcriber.isAvailable) {
          transcriberRef.current = transcriber;
          transcriber.start();
        }
      }
    } else {
      console.warn('[useVoice] No fallback STT available');
      setSttProvider('none');
      sttProviderRef.current = 'none';
    }
  }, [lang]);

  // ── VAD → STT Pipeline ──

  const startListening = useCallback(async () => {
    const provider = sttProviderRef.current;

    if (provider === 'none') {
      console.warn('[useVoice] Cannot start — no STT provider available');
      return;
    }

    // For Web Speech provider, create a SpeechTranscriber
    if (provider === 'web-speech') {
      if (!transcriberRef.current) {
        const transcriber = new SpeechTranscriber({
          lang,
          onInterim: (text) => setInterimTranscript(text),
        });
        if (!transcriber.isAvailable) {
          console.warn('[useVoice] SpeechTranscriber creation failed');
          return;
        }
        transcriberRef.current = transcriber;
      }
    }

    // Create VAD if not exists
    if (!vadRef.current) {
      vadRef.current = new VADManager({
        onSpeechStart: () => {
          setIsSpeaking(true);
          setInterimTranscript('');

          // For Web Speech, reset transcript buffer
          if (sttProviderRef.current === 'web-speech') {
            transcriberRef.current?.resetTranscript();
          }
        },
        onSpeechEnd: (audio: Float32Array) => {
          setIsSpeaking(false);

          // Fire speech audio callback (used by Voice Lock for speaker verification).
          // Runs asynchronously — does NOT block the STT flow.
          try {
            onSpeechAudioRef.current?.(audio);
          } catch (err) {
            console.warn('[useVoice] onSpeechAudio callback error:', err);
          }

          // ── Turn Arbiter mode vs Legacy mode ──
          // When onSegment is provided, we're in turn-arbiter mode:
          //   - Do NOT flush deferred TTS on every speech end
          //   - Do NOT fire onTranscript directly
          //   - Instead, route transcript to onSegment for accumulation
          //   - TTS deferral is managed turn-scoped by the FSM (accumulating state)
          //
          // When onSegment is NOT provided (legacy/fallback), keep old behavior.
          const useTurnArbiter = !!onSegmentRef.current;

          if (!useTurnArbiter) {
            // Legacy path: flush deferred TTS on every speech end
            const actor = getFSMActor();
            actor.send({ type: 'TTS_DEFER_OFF' });
            const deferred = actor.getSnapshot().context.deferredTtsTexts;
            if (deferred.length > 0) {
              actor.send({ type: 'TTS_DEFERRED_FLUSH' });
              remoteLog('tts', 'flush_deferred_speech_end', { count: deferred.length });
              const textsToSpeak = [...deferred];
              setTimeout(() => {
                for (const t of textsToSpeak) {
                  speakRef.current?.(t);
                }
              }, 0);
            }
          }
          // In turn-arbiter mode, TTS deferral stays active across intra-turn
          // pauses. It's only released on TURN_COMMITTED or TURN_CANCELLED.

          // ── STT Processing ──
          const processSTT = (sttAudio: Float32Array, options?: { gateRejected?: boolean }) => {
            const currentProvider = sttProviderRef.current;
            const gateRejected = options?.gateRejected ?? false;

            /** Route the transcript to the appropriate callback */
            const deliverTranscript = (transcript: string | null) => {
              if (!transcript) return;
              if (useTurnArbiter) {
                onSegmentRef.current?.(gateRejected ? { text: transcript, gateRejected: true } : transcript);
                return;
              }
              if (!gateRejected) {
                onTranscriptRef.current?.(transcript);
              }
            };

            if (currentProvider === 'whisper') {
              const apiKey = getOpenAIKey();
              if (!apiKey) {
                console.warn('[useVoice] No API key for Whisper, skipping');
                setInterimTranscript('');
                // Notify arbiter of empty segment so it can handle misfires
                if (useTurnArbiter) onSegmentRef.current?.('');
                return;
              }

              const wavBlob = encodeAudioBlob(sttAudio, 16000);
              setInterimTranscript('Transcribing…');

              transcribeWithWhisper(wavBlob, apiKey)
                .then((transcript) => {
                  setInterimTranscript('');
                  if (transcript) {
                    console.log('[useVoice] Whisper transcript:', transcript);
                    deliverTranscript(transcript);
                  } else {
                    console.log('[useVoice] Whisper returned empty transcript');
                    if (useTurnArbiter) onSegmentRef.current?.('');
                  }
                })
                .catch((err) => {
                  console.error('[useVoice] Whisper transcription failed:', err);
                  setInterimTranscript('');
                  if (useTurnArbiter) onSegmentRef.current?.('');
                  fallbackToWebSpeech();
                });
            } else if (currentProvider === 'web-speech') {
              const transcriber = transcriberRef.current;
              if (!transcriber) {
                setInterimTranscript('');
                if (useTurnArbiter) onSegmentRef.current?.('');
                return;
              }

              transcriber
                .finalize()
                .then((transcript) => {
                  setInterimTranscript('');
                  if (transcript) {
                    console.log('[useVoice] Web Speech transcript:', transcript);
                    deliverTranscript(transcript);
                  } else {
                    console.log('[useVoice] Web Speech: empty transcript');
                    if (useTurnArbiter) onSegmentRef.current?.('');
                  }
                })
                .catch((err) => {
                  console.warn('[useVoice] Web Speech finalize failed:', err);
                  setInterimTranscript('');
                  if (useTurnArbiter) onSegmentRef.current?.('');
                });
            }
          };

          // ── Audio Gate ──
          const gateCallback = shouldProcessAudioRef.current;
          if (gateCallback) {
            gateCallback(audio)
              .then((shouldProcess) => {
                if (!shouldProcess) {
                  remoteLog('voice', 'audio_gated', { reason: 'voice_lock' });
                  setInterimTranscript('');
                  if (!useTurnArbiter) {
                    return;
                  }
                  processSTT(audio, { gateRejected: true });
                  return;
                }
                processSTT(audio);
              })
              .catch((err) => {
                console.warn('[useVoice] Audio gate error, proceeding with STT:', err);
                processSTT(audio);
              });
          } else {
            processSTT(audio);
          }
        },
        onMisfire: () => {
          setIsSpeaking(false);
          setInterimTranscript('');

          const useTurnArbiter = !!onSegmentRef.current;

          if (!useTurnArbiter) {
            // Legacy path: flush deferred TTS on misfire
            const actor = getFSMActor();
            actor.send({ type: 'TTS_DEFER_OFF' });
            const deferred = actor.getSnapshot().context.deferredTtsTexts;
            if (deferred.length > 0) {
              actor.send({ type: 'TTS_DEFERRED_FLUSH' });
              remoteLog('tts', 'flush_deferred_misfire', { count: deferred.length });
              const textsToSpeak = [...deferred];
              setTimeout(() => {
                for (const t of textsToSpeak) {
                  speakRef.current?.(t);
                }
              }, 0);
            }
          } else {
            // Turn-arbiter mode: notify arbiter of misfire via empty segment.
            // The arbiter will cancel if no real draft exists, which triggers
            // TURN_CANCELLED in the FSM → releases TTS deferral.
            onSegmentRef.current?.('');
          }
        },
      });

      setSensitivityState(vadRef.current.threshold);
      setIsAdaptiveState(vadRef.current.isAdaptive);
    }

    // Start Web Speech transcriber (runs alongside VAD for real-time feedback)
    if (provider === 'web-speech' && transcriberRef.current) {
      transcriberRef.current.start();
    }

    await vadRef.current.start();
    setIsVADActive(true);

    console.log(`[useVoice] Voice pipeline started (provider: ${provider})`);
  }, [lang, fallbackToWebSpeech]);

  const stopListening = useCallback(() => {
    vadRef.current?.stop().catch(() => {});
    transcriberRef.current?.destroy();
    transcriberRef.current = null;
    setIsVADActive(false);
    setIsSpeaking(false);
    setInterimTranscript('');

    const useTurnArbiter = !!onSegmentRef.current;

    if (!useTurnArbiter) {
      // Legacy path: flush deferred TTS when stopping the voice pipeline
      const actor = getFSMActor();
      actor.send({ type: 'TTS_DEFER_OFF' });
      const deferred = actor.getSnapshot().context.deferredTtsTexts;
      if (deferred.length > 0) {
        actor.send({ type: 'TTS_DEFERRED_FLUSH' });
        remoteLog('tts', 'flush_deferred_stop_listening', { count: deferred.length });
        const textsToSpeak = [...deferred];
        setTimeout(() => {
          for (const t of textsToSpeak) {
            speakRef.current?.(t);
          }
        }, 0);
      }
    }
    // In turn-arbiter mode, the App.tsx layer calls arbiter.forceCancel()
    // or arbiter.forceCommit() which handles TTS deferral release via FSM.
  }, []);

  // ── Re-detect provider (after settings change) ──
  const refreshProvider = useCallback(() => {
    const newProvider = detectSttProvider();
    setSttProvider(newProvider);
    sttProviderRef.current = newProvider;
    console.log(`[useVoice] Provider refreshed: ${newProvider}`);
  }, []);

  // ── TTS Pipeline ──

  const initTtsConfig = useCallback(async () => {
    const client = wsClientRef.current;
    if (!client) return;
    const config = await fetchTtsConfig(client);
    ttsRef.current?.setConfig(config);
  }, []);

  const scheduleSpeechTask = useCallback(<T,>(
    task: (scheduledCancellationVersion: number) => Promise<T>,
  ): Promise<T> => {
    const scheduledCancellationVersion = speakCancellationVersionRef.current;
    const queuedTask = speakChainRef.current
      .catch(() => undefined)
      .then(() => task(scheduledCancellationVersion));

    speakChainRef.current = queuedTask.then(() => undefined, () => undefined);
    return queuedTask;
  }, []);

  const playQueuedText = useCallback(async (
    text: string | readonly string[],
    options?: {
      cancelIfDeferred?: boolean;
      scheduledCancellationVersion?: number;
      onChunkComplete?: (chunkIndex: number) => void;
      awaitPlayback?: boolean;
    },
  ): Promise<SpeakResult> => {
    const tts = ttsRef.current;
    const queue = audioQueueRef.current;
    if (!tts || !queue) {
      remoteLog('tts', 'speak_no_ref', { hasTts: !!tts, hasQueue: !!queue });
      return 'cancelled';
    }

    const rawChunks = Array.isArray(text) ? text : [text];
    const cleanChunks = rawChunks.map((chunk) => stripMarkdownForTTS(chunk).trim());
    const joinedCleanText = cleanChunks.filter(Boolean).join('\n\n').trim();
    const originalText = typeof text === 'string' ? text : rawChunks.join('\n\n');
    if (!joinedCleanText) {
      remoteLog('tts', 'speak_empty', { original: originalText });
      return 'completed';
    }

    const shouldCancelIfDeferred = options?.cancelIfDeferred ?? false;
    const shouldAwaitPlayback = options?.awaitPlayback ?? true;
    const scheduledCancellationVersion = options?.scheduledCancellationVersion
      ?? speakCancellationVersionRef.current;
    const isCancelled = (): boolean => scheduledCancellationVersion !== speakCancellationVersionRef.current;
    const getCancelledResult = (): SpeakResult => (
      mutedCancellationVersionRef.current === speakCancellationVersionRef.current || syncSpeakerMutedRef()
        ? 'completed'
        : 'cancelled'
    );

    if (isCancelled()) {
      remoteLog('tts', 'speak_cancelled_before_start', {
        text: joinedCleanText.substring(0, 60),
        tracked: shouldCancelIfDeferred,
      });
      return getCancelledResult();
    }

    if (syncSpeakerMutedRef()) {
      remoteLog('tts', 'speak_suppressed_muted', {
        text: joinedCleanText.substring(0, 60),
        tracked: shouldCancelIfDeferred,
      });
      return 'completed';
    }

    const snapshot = getFSMActor().getSnapshot();
    if (snapshot.context.ttsAutoPlayDeferred) {
      if (shouldCancelIfDeferred) {
        remoteLog('tts', 'speak_tracked_cancelled_deferred', {
          text: joinedCleanText.substring(0, 60),
        });
        return 'cancelled';
      }

      getFSMActor().send({ type: 'TTS_SEGMENT_DEFER', text: originalText });
      remoteLog('tts', 'speak_deferred', { text: joinedCleanText.substring(0, 60) });
      return 'completed';
    }

    remoteLog('tts', 'speak_start', {
      text: joinedCleanText.substring(0, 80),
      provider: tts.provider,
      tracked: shouldCancelIfDeferred,
      chunkCount: cleanChunks.length,
    });

    type PreparedSentence = {
      text: string;
      playbackTag: string;
      playbackFinished: Promise<void>;
      audioData: ArrayBuffer | null;
      completeSynthesis: () => void;
      ttsError?: VisibleTtsError;
    };

    const prepareSentence = async (trimmed: string): Promise<PreparedSentence> => {
      const playbackTag = `tts-${++speakTagCounterRef.current}`;
      const playbackFinished = new Promise<void>((resolve) => {
        playbackWaitersRef.current.set(playbackTag, resolve);
      });

      let synthesisCompleted = false;
      const completeSynthesis = () => {
        if (synthesisCompleted) return;
        synthesisCompleted = true;
        queue.markSynthesisEnd();
      };

      queue.markSynthesisStart();

      try {
        const audioData = await tts.synthesize(trimmed);
        return {
          text: trimmed,
          playbackTag,
          playbackFinished,
          audioData,
          completeSynthesis,
        };
      } catch (error) {
        const visibleError = getVisibleTtsError(error);
        if (visibleError) {
          remoteLog('tts', 'visible_error', {
            code: visibleError.code,
            provider: visibleError.provider,
            status: visibleError.status,
            message: visibleError.userMessage,
          });
          return {
            text: trimmed,
            playbackTag,
            playbackFinished,
            audioData: null,
            completeSynthesis,
            ttsError: visibleError,
          };
        }
        playbackWaitersRef.current.delete(playbackTag);
        completeSynthesis();
        throw error;
      }
    };

    const discardPreparedSentence = (preparedSentencePromise: Promise<PreparedSentence> | null) => {
      if (!preparedSentencePromise) return;
      void preparedSentencePromise
        .then((preparedSentence) => {
          playbackWaitersRef.current.delete(preparedSentence.playbackTag);
          preparedSentence.completeSynthesis();
        })
        .catch(() => undefined);
    };

    for (let chunkIndex = 0; chunkIndex < cleanChunks.length; chunkIndex += 1) {
      const cleanChunk = cleanChunks[chunkIndex]!;
      if (!cleanChunk) {
        options?.onChunkComplete?.(chunkIndex);
        continue;
      }

      const sentences = splitIntoSentences(cleanChunk)
        .map((sentence) => sentence.trim())
        .filter(Boolean);

      let preparedSentencePromise: Promise<PreparedSentence> | null = null;

      for (let sentenceIndex = 0; sentenceIndex < sentences.length; sentenceIndex += 1) {
        const trimmed = sentences[sentenceIndex]!;
        if (syncSpeakerMutedRef()) {
          discardPreparedSentence(preparedSentencePromise);
          remoteLog('tts', 'speak_suppressed_muted_mid_batch', {
            text: trimmed.substring(0, 60),
            tracked: shouldCancelIfDeferred,
          });
          return 'completed';
        }
        if (isCancelled()) {
          discardPreparedSentence(preparedSentencePromise);
          return getCancelledResult();
        }

        if (shouldCancelIfDeferred && getFSMActor().getSnapshot().context.ttsAutoPlayDeferred) {
          discardPreparedSentence(preparedSentencePromise);
          remoteLog('tts', 'speak_tracked_cancelled_mid_batch', {
            text: trimmed.substring(0, 60),
          });
          return 'cancelled';
        }

        const provider = tts.provider;

        if (provider === 'webspeech') {
          webSpeechRef.current?.cancel();
          const handle = speakWithWebSpeech(trimmed, queue.speed);
          webSpeechRef.current = handle;
          setIsTTSPlaying(true);
          await handle.finished;
          if (webSpeechRef.current === handle) {
            webSpeechRef.current = null;
            setIsTTSPlaying(false);
          }
          if (isCancelled()) return getCancelledResult();
          continue;
        }

        const currentPreparedPromise = preparedSentencePromise ?? prepareSentence(trimmed);
        const nextSentence = sentences[sentenceIndex + 1];

        const preparedSentence = await currentPreparedPromise;

        if (isCancelled()) {
          discardPreparedSentence(preparedSentencePromise);
          playbackWaitersRef.current.delete(preparedSentence.playbackTag);
          preparedSentence.completeSynthesis();
          return getCancelledResult();
        }

        if (preparedSentence.audioData && preparedSentence.audioData.byteLength > 0) {
          remoteLog('tts', 'enqueue', {
            chunk: preparedSentence.text.substring(0, 60),
            bytes: preparedSentence.audioData.byteLength,
            provider: tts.provider,
            pending: queue.pendingSynthesis,
            qLen: queue.length,
            tag: preparedSentence.playbackTag,
          });
          queue.enqueue({ audioData: preparedSentence.audioData, tag: preparedSentence.playbackTag });
          preparedSentence.completeSynthesis();
          preparedSentencePromise = nextSentence ? prepareSentence(nextSentence) : null;
          if (shouldAwaitPlayback) {
            await preparedSentence.playbackFinished;
            if (isCancelled()) {
              discardPreparedSentence(preparedSentencePromise);
              return getCancelledResult();
            }
          }
        } else {
          playbackWaitersRef.current.delete(preparedSentence.playbackTag);
          preparedSentence.completeSynthesis();
          if (preparedSentence.ttsError) {
            onTtsError?.(preparedSentence.ttsError.userMessage);
          }
          remoteLog('tts', 'enqueue_empty', {
            text: preparedSentence.text,
            bytes: preparedSentence.audioData?.byteLength ?? 0,
            error: preparedSentence.ttsError?.code,
            status: preparedSentence.ttsError?.status,
          });
          console.warn(`[useVoice] ${provider} synthesis returned null for: "${preparedSentence.text}" — falling back to Web Speech`);
          preparedSentencePromise = nextSentence ? prepareSentence(nextSentence) : null;
          if (isWebSpeechAvailable()) {
            webSpeechRef.current?.cancel();
            const handle = speakWithWebSpeech(preparedSentence.text, queue.speed);
            webSpeechRef.current = handle;
            setIsTTSPlaying(true);
            await handle.finished;
            if (webSpeechRef.current === handle) {
              webSpeechRef.current = null;
              setIsTTSPlaying(false);
            }
            if (isCancelled()) {
              discardPreparedSentence(preparedSentencePromise);
              return getCancelledResult();
            }
          }
        }
      }

      options?.onChunkComplete?.(chunkIndex);
    }

    return 'completed';
  }, [onTtsError]);

  const speak = useCallback((text: string) => {
    void scheduleSpeechTask(async (scheduledCancellationVersion) => {
      await playQueuedText(text, { scheduledCancellationVersion, awaitPlayback: false });
    }).catch((err) => {
      console.warn('[useVoice] speak chain error:', err);
      remoteLog('tts', 'speak_chain_error', { error: String(err) });
    });
  }, [playQueuedText, scheduleSpeechTask]);

  const speakTracked = useCallback((text: string | readonly string[], options?: SpeakTrackedOptions) => {
    return scheduleSpeechTask(async (scheduledCancellationVersion) => playQueuedText(text, {
      cancelIfDeferred: true,
      scheduledCancellationVersion,
      onChunkComplete: options?.onChunkComplete,
    }))
      .catch((err) => {
        console.warn('[useVoice] tracked speak error:', err);
        remoteLog('tts', 'speak_chain_error', { error: String(err), tracked: true });
        return 'cancelled' as const;
      });
  }, [playQueuedText, scheduleSpeechTask]);

  // Keep speakRef in sync so flush code (defined before speak) can call it.
  speakRef.current = speak;

  const stopSpeaking = useCallback(() => {
    stopSpeakingInternal('explicit_stop');
  }, [stopSpeakingInternal]);

  const pauseSpeaking = useCallback(() => {
    audioQueueRef.current?.pause();
    setIsTTSPaused(true);
  }, []);

  const resumeSpeaking = useCallback(() => {
    audioQueueRef.current?.resume();
    setIsTTSPaused(false);
  }, []);

  const skipSegment = useCallback(() => {
    audioQueueRef.current?.skip();
  }, []);

  const setPlaybackSpeed = useCallback((speed: number) => {
    audioQueueRef.current?.setSpeed(speed);
    setPlaybackSpeedState(speed);
  }, []);

  const cycleSpeed = useCallback(() => {
    const newSpeed = audioQueueRef.current?.cycleSpeed();
    if (newSpeed !== undefined) {
      setPlaybackSpeedState(newSpeed);
    }
  }, []);

  const setSensitivity = useCallback((threshold: number) => {
    vadRef.current?.setSensitivity(threshold);
    setSensitivityState(threshold);
  }, []);

  const setAdaptive = useCallback((enabled: boolean) => {
    vadRef.current?.setAdaptive(enabled);
    setIsAdaptiveState(enabled);
  }, []);

  const resetVoiceLock = useCallback((runId?: string) => {
    ttsRef.current?.resetVoiceLock(runId);
  }, []);

  // Memoize the return object so consumers get a stable reference.
  // Without this, every render creates a new object, which triggers
  // useEffects in App.tsx that depend on `voice` — causing infinite
  // re-render loops (React error #185) when any voice state changes.
  return useMemo(() => ({
    isVADActive,
    isSpeaking,
    isTTSPlaying,
    isTTSPaused,
    sttProvider,
    sttAvailable,
    interimTranscript,
    playbackSpeed,
    sensitivity,
    isAdaptive,
    startListening,
    stopListening,
    speak,
    speakTracked,
    stopSpeaking,
    pauseSpeaking,
    resumeSpeaking,
    skipSegment,
    setPlaybackSpeed,
    cycleSpeed,
    setSensitivity,
    setAdaptive,
    initTtsConfig,
    refreshProvider,
    resetVoiceLock,
  }), [
    isVADActive,
    isSpeaking,
    isTTSPlaying,
    isTTSPaused,
    sttProvider,
    sttAvailable,
    interimTranscript,
    playbackSpeed,
    sensitivity,
    isAdaptive,
    startListening,
    stopListening,
    speak,
    speakTracked,
    stopSpeaking,
    pauseSpeaking,
    resumeSpeaking,
    skipSegment,
    setPlaybackSpeed,
    cycleSpeed,
    setSensitivity,
    setAdaptive,
    initTtsConfig,
    refreshProvider,
    resetVoiceLock,
  ]);
}
